// Our own server (the paid-service gate, _gate.php) refuses a lookup once
// today's allowance for that service is used up, answering
// { error: { code: "daily_limit_reached", message } }. That is not an outage:
// nothing was sent to the provider, and it will work again tomorrow.
//
// Before this (2026-10-13) the apps treated it like any other failure:
//   - logged an error for every single place, and told the user nothing or
//     something wrong ("Couldn't find places…");
//   - fell back to the next provider — so hitting the cheap service's limit
//     pushed the same traffic onto Google, the expensive one.
//
// Now a limit answer is recorded here and the chain stops. Each screen asks
// once, at the end of its action, whether to tell the user — one message per
// action, never one per place. Matches mobile's Common/GateLimit.js.

let pending = false;

export const isDailyLimit = (error) => {
  if (!error) return false;
  if (typeof error === "object" && error.code === "daily_limit_reached") return true;
  const text = typeof error === "string" ? error : error.message || "";
  return /daily limit reached/i.test(text);
};

// Called by the lookup that was refused.
export const noteDailyLimit = () => { pending = true; };

// True once per batch of refusals: reading it clears it.
export const takeDailyLimitNotice = () => {
  const hit = pending;
  pending = false;
  return hit;
};

// Start an action with a clean slate, so an earlier action's refusal isn't
// reported against this one.
export const clearDailyLimitNotice = () => { pending = false; };

// Every limit message gives the true reason in one sentence (a limit with no
// reason feels arbitrary) and offers "How Andiamo stays free", which opens
// the Partner Offers page — that page explains what costs money and how
// booking through partner links helps. Deliberately NOT a pitch inside the
// message itself: buying from a partner doesn't lift the limit, and saying
// the two in one breath would imply it does. Decided 2026-10-13.
//
// Reworded 2026-10-16: the limit is per network (shared Wi-Fi shares it), it
// resets at midnight UTC — shown as the user's own local time, not
// "tomorrow" — and the search message offers "From existing guides".
const WHY = "Andiamo is free, and each lookup costs us a little, so each network has a daily limit.";

// When today's allowance comes back: the server counts per UTC day
// (_gate.php), so the next UTC midnight, in the user's own time — e.g. "5:00 PM".
export const limitResetTime = (now = new Date()) =>
  new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

// Built when read, so the reset time is current.
export const LIMIT_MESSAGES = {
  hours: {
    title: "Opening times not added",
    get body() { return `${WHY} Today's limit has been reached, so some places have no opening times yet. Your trip is ready — add them after ${limitResetTime()} with Find Opening Times in the Places menu.`; },
  },
  hoursOnly: {
    title: "Opening times not added",
    get body() { return `${WHY} Today's limit has been reached, so some of these places have no opening times yet. Please try again after ${limitResetTime()}.`; },
  },
  // Build My Trip shows "search" with a "Use existing guides" button, and
  // "searchLater" when it can't offer one (see BuildMyTripWizard).
  search: {
    title: "Today's search limit reached",
    get body() { return `${WHY} It resets at ${limitResetTime()}.\n\nYou can still build this trip now from existing guides, yours or our Featured ones, without a search: tap Use existing guides and choose a guide for each destination.`; },
  },
  searchLater: {
    title: "Today's search limit reached",
    get body() { return `${WHY} It resets at ${limitResetTime()}.\n\nYou can still build a trip without a search: start a new Build My Trip and choose From existing guides, then a guide (yours or our Featured ones) for each destination.`; },
  },
  ai: {
    title: "Today's AI limit reached",
    get body() { return `Andiamo is free, and each AI request costs us a little, so each network has a daily limit. It resets at ${limitResetTime()}. To see how Andiamo stays free, open Partner Offers in the menu.`; },
  },
};

const PARTNER_OFFERS_URL = "/Docs/Tickets.html";
// The "How Andiamo stays free" button: the Partner Offers page (also in the menu).
export const openPartnerOffers = () => window.open(PARTNER_OFFERS_URL, "_blank", "noopener");
const NOTICE_ID = "gate-limit-notice";

// One notice for the action, shown as a small card at the bottom of the
// window. Built directly in the page rather than through each screen's own
// alert dialog, because the things that hit a limit (Build My Trip's wizard,
// the Places menu, the Schedule) each have a separate dialog, and the
// wizard has usually closed by the time its enrichment finishes. It doesn't
// block anything and can be dismissed.
export const showLimitNotice = (kind) => {
  if (typeof document === "undefined") return;
  const m = LIMIT_MESSAGES[kind] || LIMIT_MESSAGES.hours;
  const old = document.getElementById(NOTICE_ID);
  if (old) old.remove();
  const el = document.createElement("div");
  el.id = NOTICE_ID;
  el.setAttribute("role", "status");
  el.style.cssText = "position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:20000;max-width:min(560px,calc(100vw - 32px));background:#fff;color:#1b1f27;border:1px solid #dde1e8;border-radius:12px;box-shadow:0 12px 32px rgba(0,0,0,.18);padding:14px 16px;font:14px/1.45 system-ui,-apple-system,'Segoe UI',sans-serif;";
  const title = document.createElement("div");
  title.textContent = m.title;
  title.style.cssText = "font-weight:700;margin-bottom:4px;";
  const body = document.createElement("div");
  body.textContent = m.body;
  body.style.whiteSpace = "pre-line";
  const row = document.createElement("div");
  row.style.cssText = "display:flex;gap:8px;justify-content:flex-end;margin-top:10px;flex-wrap:wrap;";
  const btn = (label, primary, onClick) => {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = label;
    b.style.cssText = `font:inherit;padding:7px 12px;border-radius:8px;cursor:pointer;border:1px solid ${primary ? "#1d6af5" : "#dde1e8"};background:${primary ? "#1d6af5" : "#fff"};color:${primary ? "#fff" : "#1b1f27"};`;
    b.addEventListener("click", onClick);
    return b;
  };
  row.appendChild(btn("How Andiamo stays free", false, openPartnerOffers));
  row.appendChild(btn("OK", true, () => el.remove()));
  el.appendChild(title);
  el.appendChild(body);
  el.appendChild(row);
  document.body.appendChild(el);
};
